import { ValueObject } from './ValueObject';

export const searchOperators = [`eq`, `contains`, `ne`, `gt`, `gte`, `lt`, `lte`, `in`, `nin`] as const;
export type SearchOperator = (typeof searchOperators)[number];

export const searchTypes = [`string`, `number`, `date`, `boolean`, `geojson`, `array`, `id`] as const;
export type SearchType = (typeof searchTypes)[number];

const allowedOperatorsByType: { [Key in SearchType]: SearchOperator[] } = {
  string: [`eq`, `contains`, `ne`], // eslint-disable-line id-blacklist
  number: [`eq`, `ne`, `gt`, `gte`, `lt`, `lte`], // eslint-disable-line id-blacklist
  date: [`eq`, `ne`, `gt`, `gte`, `lt`, `lte`],
  boolean: [`eq`, `ne`], // eslint-disable-line id-blacklist
  geojson: [`in`, `nin`],
  array: [`in`, `nin`],
  id: [`eq`, `ne`],
};

export interface SearchPredicateProps {
  field: string;
  operator: SearchOperator;
  type: SearchType;
  value: any;
}

export class SearchPredicate extends ValueObject<SearchPredicateProps> {
  get field(): string {
    return this.props.field;
  }

  get operator(): SearchOperator {
    return this.props.operator;
  }

  get type(): SearchType {
    return this.props.type;
  }

  get value(): string | number | number[][] | string[] | boolean | null {
    return this.props.value;
  }

  private constructor(props: SearchPredicateProps) {
    super(props);
  }

  setValue(value: string | number | number[][] | string[] | null) {
    this.props.value = value;
  }

  static create(props: SearchPredicateProps): SearchPredicate {
    if (!searchOperators.includes(props.operator)) {
      throw `Invalid search operator: ${props.operator}`;
    }
    if (!searchTypes.includes(props.type)) {
      throw `Invalid search type: ${props.type}`;
    }
    if (!allowedOperatorsByType[props.type].includes(props.operator)) {
      throw `Invalid search operator "${props.operator}" for type "${props.type}"`;
    }
    return new SearchPredicate(props);
  }

  static predicatesToBase64(predicates: SearchPredicate[]): string {
    if (predicates.length === 0) {
      return ``;
    }

    const mappedPredicates = predicates.map((p) => p.getValue());
    const stringified = JSON.stringify(mappedPredicates);
    const base64 = Buffer.from(stringified, `utf-8`).toString(`base64`);
    return base64;
  }
}
